import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { trackMetaLead } from '../utils/metaPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_STATE = {
  asset_type: '',
  asset_value: '',
  business_abn: '',
  time_in_business: '',
  suburb: '',
};

export default function QuoteLeadForm({ sourcePage, compact = false, dataTestIdPrefix = 'quote-form' }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    Object.entries(formData).forEach(([field, value]) => {
      if (!value.trim()) nextErrors[field] = 'Required';
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, {
        ...formData,
        source_page: sourcePage,
      });

      if (window.gtag) {
        window.gtag('event', 'lead_submit', {
          event_category: 'quote',
          event_label: sourcePage,
        });
      }
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'lead_submit', source_page: sourcePage });
      }

      trackMetaLead({
        content_name: 'Quote Request Submitted',
        content_category: 'quote_request',
        source_page: sourcePage,
      });

      setFormData(INITIAL_STATE);
      navigate('/quote-confirmation');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not submit quote request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${compact ? '' : 'rounded-2xl border border-purple-200 bg-white p-4 sm:p-5'}`} data-testid={`${dataTestIdPrefix}-container`}>
      <div>
        <Label className="text-xs uppercase tracking-[0.12em] text-[#6C657E]" htmlFor={`${dataTestIdPrefix}-asset-type`}>Asset type</Label>
        <Input
          id={`${dataTestIdPrefix}-asset-type`}
          value={formData.asset_type}
          onChange={(event) => updateField('asset_type', event.target.value)}
          className="mt-1 h-11 border-purple-200 bg-white text-[#1B1431]"
          data-testid={`${dataTestIdPrefix}-asset-type`}
        />
        {errors.asset_type && <p className="mt-1 text-xs text-red-600" data-testid={`${dataTestIdPrefix}-error-asset-type`}>{errors.asset_type}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#6C657E]" htmlFor={`${dataTestIdPrefix}-asset-value`}>Asset value</Label>
          <Input
            id={`${dataTestIdPrefix}-asset-value`}
            value={formData.asset_value}
            onChange={(event) => updateField('asset_value', event.target.value)}
            className="mt-1 h-11 border-purple-200 bg-white text-[#1B1431]"
            data-testid={`${dataTestIdPrefix}-asset-value`}
          />
          {errors.asset_value && <p className="mt-1 text-xs text-red-600" data-testid={`${dataTestIdPrefix}-error-asset-value`}>{errors.asset_value}</p>}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#6C657E]" htmlFor={`${dataTestIdPrefix}-business-abn`}>Business ABN</Label>
          <Input
            id={`${dataTestIdPrefix}-business-abn`}
            value={formData.business_abn}
            onChange={(event) => updateField('business_abn', event.target.value)}
            className="mt-1 h-11 border-purple-200 bg-white text-[#1B1431]"
            data-testid={`${dataTestIdPrefix}-business-abn`}
          />
          {errors.business_abn && <p className="mt-1 text-xs text-red-600" data-testid={`${dataTestIdPrefix}-error-business-abn`}>{errors.business_abn}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#6C657E]" htmlFor={`${dataTestIdPrefix}-time-in-business`}>Time in business</Label>
          <Input
            id={`${dataTestIdPrefix}-time-in-business`}
            value={formData.time_in_business}
            onChange={(event) => updateField('time_in_business', event.target.value)}
            className="mt-1 h-11 border-purple-200 bg-white text-[#1B1431]"
            data-testid={`${dataTestIdPrefix}-time-in-business`}
          />
          {errors.time_in_business && <p className="mt-1 text-xs text-red-600" data-testid={`${dataTestIdPrefix}-error-time-in-business`}>{errors.time_in_business}</p>}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#6C657E]" htmlFor={`${dataTestIdPrefix}-suburb`}>Suburb</Label>
          <Input
            id={`${dataTestIdPrefix}-suburb`}
            value={formData.suburb}
            onChange={(event) => updateField('suburb', event.target.value)}
            className="mt-1 h-11 border-purple-200 bg-white text-[#1B1431]"
            data-testid={`${dataTestIdPrefix}-suburb`}
          />
          {errors.suburb && <p className="mt-1 text-xs text-red-600" data-testid={`${dataTestIdPrefix}-error-suburb`}>{errors.suburb}</p>}
        </div>
      </div>

      <Button type="submit" disabled={submitting} className="w-full rounded-full bg-purple-600 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid={`${dataTestIdPrefix}-submit`}>
        {submitting ? 'Submitting...' : 'Get my rate in 60 seconds'}
        {!submitting && <ArrowRight size={14} className="ml-2" />}
      </Button>
    </form>
  );
}
